import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GstReport from './GstReport';
import TdsReport from './TdsReport';
import TdsReceivableReport from './TdsReceivableReport';
import SurvivalPrediction from './SurvivalPrediction';
import ConsolidatedReport from './ConsolidatedReport';
import ProfitLossReport from './ProfitLossReport';
import BalanceSheetReport from './BalanceSheetReport';
import { FaUser, FaStore } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

interface Invoice {
  id: number;
  Company_id: number;
  Customer_id: number;
  InvoiceNo: string;
  Date: string;
  Particulars: string;
  Total_Amount: number;
  paid: number | boolean;
  cancelled: number | boolean;
}

interface PurchaseInvoice {
  id: number;
  Company_id: number;
  Vendor_id: number;
  InvoiceNo: string;
  Date: string;
  Particulars: string;
  Total_Amount: number;
  TDSAmount: number;
  paid: number | boolean;
}

interface Customer {
  id: number;
  company_name: string;
}

interface Vendor {
  id: number;
  company_name: string;
}

const Reports: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<PurchaseInvoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);

  // Visibility toggle states for report segments
  const [showOverview, setShowOverview] = useState(true);
  const [showConsolidated, setShowConsolidated] = useState(true);
  const [showProfitLoss, setShowProfitLoss] = useState(true);
  const [showBalanceSheet, setShowBalanceSheet] = useState(true);
  const [showTaxSummary, setShowTaxSummary] = useState(true);
  const [showSurvival, setShowSurvival] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  // Helper function to decode JWT token
  const decodeToken = (token: any) => {
    if (!token) return {};
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const storedRole = localStorage.getItem("role");
      let decoded: any = null;
      if (token) {
        decoded = decodeToken(token);
      }
      let compId: string | number | null = null;
      if (decoded) {
        compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      }

      const [compRes, invRes, purRes, custRes, vendRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/customers`),
        axios.get(`${API_URL}/auth/vendors`)
      ]);

      const allComps = compRes.data || [];
      if (storedRole !== "super-admin" && compId) {
        const filteredCompanies = allComps.filter((c: any) => String(c.id) === String(compId));
        setCompanies(filteredCompanies);
        setSelectedCompanyId(String(compId));
      } else {
        const selectedCompanyHeader = localStorage.getItem("selected_company_id");
        if (selectedCompanyHeader && selectedCompanyHeader !== "all") {
          setCompanies(allComps.filter((c: any) => String(c.id) === String(selectedCompanyHeader)));
          setSelectedCompanyId(selectedCompanyHeader);
        } else {
          setCompanies(allComps);
        }
      }

      setInvoices(invRes.data || []);
      setPurchaseInvoices(purRes.data || []);
      setCustomers(custRes.data || []);
      setVendors(vendRes.data || []);
    } catch (err) {
      console.error("Failed to load reports base data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Customer outstanding calculation (filtered by company if selected)
  const customerOutstandingTotal = React.useMemo(() => {
    return invoices
      .filter(inv => {
        if (inv.cancelled === 1 || inv.cancelled === true) return false;
        if (inv.paid === 1 || inv.paid === true) return false;
        if (selectedCompanyId && String(inv.Company_id) !== String(selectedCompanyId)) return false;
        return true;
      })
      .reduce((sum, inv) => sum + Number(inv.Total_Amount || 0), 0);
  }, [invoices, selectedCompanyId]);

  // Vendor net outstanding calculation (filtered by company if selected)
  const vendorOutstandingTotal = React.useMemo(() => {
    return purchaseInvoices
      .filter(inv => {
        if (inv.paid === 1 || inv.paid === true) return false;
        if (selectedCompanyId && String(inv.Company_id) !== String(selectedCompanyId)) return false;
        return true;
      })
      .reduce((sum, inv) => sum + (Number(inv.Total_Amount || 0) - Number(inv.TDSAmount || 0)), 0);
  }, [purchaseInvoices, selectedCompanyId]);

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 bg-white p-6 rounded-3xl border border-blue-100 shadow-sm">
        <div>
          <h2 className="text-3xl font-extrabold text-blue-900 tracking-tight">Unified Financial Dashboard</h2>
          <p className="text-gray-500 mt-1">Elaborative, single-view panel presenting all expense heads, profit & loss, balance sheets, and tax status details in one view.</p>
        </div>

        {/* Global Company Selector */}
        <div className="min-w-[200px]">
          <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 tracking-wider">Filter Reports by Company</label>
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-bold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {companies.length > 1 && <option value="">All Companies Combined</option>}
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Visibility Filters Box */}
      <div className="mb-8 bg-blue-50/30 border border-blue-100 rounded-3xl p-5 shadow-sm">
        <h4 className="text-xs font-black text-blue-900/80 mb-3.5 uppercase tracking-wider">Select Reports to Display</h4>
        <div className="flex flex-wrap gap-3 text-xs font-bold text-blue-950">
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showOverview}
              onChange={e => setShowOverview(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Overview Metrics
          </label>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showConsolidated}
              onChange={e => setShowConsolidated(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Consolidated Expenses
          </label>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showProfitLoss}
              onChange={e => setShowProfitLoss(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Profit & Loss Statement
          </label>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showBalanceSheet}
              onChange={e => setShowBalanceSheet(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Balance Sheet
          </label>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showTaxSummary}
              onChange={e => setShowTaxSummary(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            GST/TDS Summary
          </label>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-blue-100 hover:bg-blue-50 transition shadow-sm">
            <input
              type="checkbox"
              checked={showSurvival}
              onChange={e => setShowSurvival(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Survival & TDS Receivable
          </label>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 font-medium text-sm">Loading unified report dashboard...</div>
      ) : (
        <div className="space-y-10">
          {/* Key Outstanding Metrics Grid */}
          {showOverview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-orange-100 rounded-3xl p-6 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-orange-500 text-white rounded-2xl shadow-md">
                    <FaStore size={24} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-extrabold text-orange-800/70">Total Vendor Net Pendency</div>
                    <div className="text-2xl font-black text-orange-900 mt-1">
                      ₹{vendorOutstandingTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-teal-100 rounded-3xl p-6 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-teal-500 text-white rounded-2xl shadow-md">
                    <FaUser size={24} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-extrabold text-teal-800/70">Total Customer Outstanding</div>
                    <div className="text-2xl font-black text-teal-900 mt-1">
                      ₹{customerOutstandingTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 1. Consolidated Expenses & Earnings */}
          {showConsolidated && (
            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm overflow-x-auto p-1 min-w-0">
              <div className="w-full min-w-[700px] sm:min-w-0">
                <ConsolidatedReport />
              </div>
            </div>
          )}

          {/* 2. Profit & Loss Statement */}
          {showProfitLoss && (
            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm overflow-x-auto p-1 min-w-0">
              <div className="w-full min-w-[700px] sm:min-w-0">
                <ProfitLossReport />
              </div>
            </div>
          )}

          {/* 3. Balance Sheet */}
          {showBalanceSheet && (
            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm overflow-x-auto p-1 min-w-0">
              <div className="w-full min-w-[700px] sm:min-w-0">
                <BalanceSheetReport />
              </div>
            </div>
          )}

          {/* 4. GST & TDS Tax Reports Row */}
          {showTaxSummary && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0 overflow-hidden">
              <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-4 sm:p-6 overflow-x-auto min-w-0">
                <h3 className="font-extrabold text-blue-900 text-xl mb-4 border-b border-gray-100 pb-3">GST Report Details</h3>
                <div className="w-full min-w-[600px] sm:min-w-0">
                  <GstReport />
                </div>
              </div>
              <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-4 sm:p-6 overflow-x-auto min-w-0">
                <h3 className="font-extrabold text-blue-900 text-xl mb-4 border-b border-gray-100 pb-3">TDS Report Details</h3>
                <div className="w-full min-w-[600px] sm:min-w-0">
                  <TdsReport />
                </div>
              </div>
            </div>
          )}

          {/* 5. TDS Receivable & Survival Prediction */}
          {showSurvival && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0 overflow-hidden">
              <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-4 sm:p-6 overflow-x-auto min-w-0">
                <h3 className="font-extrabold text-blue-900 text-xl mb-4 border-b border-gray-100 pb-3">TDS Receivable Status</h3>
                <div className="w-full min-w-[600px] sm:min-w-0">
                  <TdsReceivableReport />
                </div>
              </div>
              <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-4 sm:p-6 overflow-x-auto min-w-0">
                <h3 className="font-extrabold text-blue-900 text-xl mb-4 border-b border-gray-100 pb-3">Survival Prediction Analysis</h3>
                <div className="w-full min-w-[600px] sm:min-w-0">
                  <SurvivalPrediction />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
